import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import PersonaRoutine, { Moment } from '@comp/persona/persona-routine'
import PersonaHelpLinks, { HelpLinkGroup } from '@comp/persona/persona-help-links'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  routineEyebrow: string
  routineTitle: string
  routineBody: string
  moments: Moment[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  helpEyebrow: string
  helpTitle: string
  helpBody: string
  helpGroups: HelpLinkGroup[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Monofor for developers',
    metaDescription:
      'OAuth 2, OIDC, SAML, JWT, SCIM, and a clean REST API. Self-hosted or cloud: same endpoints, same shape, no lock-in.',
    heroEyebrow: 'For developers',
    heroTitle: ['Identity APIs', 'you will not fight.'],
    heroBody:
      'OAuth 2, OIDC, SAML, JWT, SCIM, and a clean REST surface: same shape self-hosted or cloud. Standards first. No SDK gymnastics.',
    badges: ['OAuth 2 / OIDC / SAML 2 / JWT', 'OpenAPI 3 REST surface', 'Custom REST connector'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    routineEyebrow: 'A day with Monofor',
    routineTitle: "A developer's week with Monofor.",
    routineBody:
      'Less time arguing with auth, more time shipping product. Identity becomes a backend you can script against.',
    moments: [
      {
        time: 'Tuesday',
        title: 'Add SSO to the new internal tool',
        body: 'You pick OIDC, register a Monosign client, drop the discovery URL into your framework, and the redirect dance just works. Five-minute setup, no SDK to learn.',
      },
      {
        time: 'Thursday',
        title: 'Wire identity-as-code into CI',
        body: 'You script user and group provisioning through the REST API, pin it to idempotency keys, and run it in your pipeline. Every environment ships with the right access by default.',
      },
      {
        time: 'Friday',
        title: 'Hook the audit stream into the SIEM',
        body: 'A webhook subscription picks up identity events; you sign-verify them and ship them straight into your SIEM. No batch jobs, no polling.',
      },
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What developers get with Monofor.',
    pillarsBody:
      'Four properties that make Monofor feel like a service, not a vendor: standards, REST, custom-app friendliness, and deployment parity.',
    pillars: [
      {
        title: 'Standards first, no proprietary tokens',
        description:
          'OAuth 2, OpenID Connect, SAML 2, JWT, SCIM: the protocols you already know. Drop Monofor in and keep your auth code portable.',
        bullets: [
          'OAuth 2 authorization-code, PKCE, client-credentials',
          'OpenID Connect with discovery and JWKS',
          'SAML 2 with ADFS-compatible assertions',
          'JWT issuance and validation',
        ],
      },
      {
        title: 'A REST API you can actually script against',
        description:
          'Users, groups, entitlements, devices, audits: all reachable through a clean REST surface. OpenAPI spec, cursor pagination, idempotency keys.',
        bullets: [
          'OpenAPI 3 spec, downloadable',
          'Idempotency keys for safe retries',
          'Cursor-based pagination, predictable filters',
          'Webhook events on identity change',
        ],
      },
      {
        title: 'Custom apps connect in an afternoon',
        description:
          'Got an in-house app the catalog does not cover? The custom REST integration turns any HTTP-speaking app into an SSO target.',
        bullets: [
          'Custom REST application connector',
          'Custom OIDC application support',
          'Browser-extension fallback for legacy apps',
          'API-based sign-in for service accounts',
        ],
      },
      {
        title: 'Same shape, self-hosted or cloud',
        description:
          'Develop against a local Monosign tenant, ship to your own VPC, or run as a managed cloud tenant: same endpoints, same shape, same auth.',
        bullets: [
          'Docker, Helm, and bare-metal deployment',
          'Kubernetes-native Monopam operator',
          'Sandbox tenants for CI',
          'Configuration-as-code patterns',
        ],
      },
    ],
    helpEyebrow: 'Straight to the docs',
    helpTitle: 'Straight into the docs.',
    helpBody:
      'Deep-links into the help articles that engineers actually paste into PRs.',
    helpGroups: [
      {
        title: 'Standards & protocols',
        links: [
          { label: 'OAuth 2 integration', description: 'Authorization-code flow walkthrough', href: 'https://help.monofor.com/monosign/oauth-integration' },
          { label: 'OpenID Connect integrations', description: 'OIDC setup and providers', href: 'https://help.monofor.com/monosign/openid-oidc-integrations' },
          { label: 'SAML 2.0 integrations', description: 'SAML setup and providers', href: 'https://help.monofor.com/monosign/saml-2.0-integrations' },
          { label: 'JWT integrations', description: 'JWT issuance and validation', href: 'https://help.monofor.com/monosign/jwt-integrations' },
        ],
      },
      {
        title: 'APIs & automation',
        links: [
          { label: 'REST API overview', description: 'Endpoints, auth, conventions', href: 'https://help.monofor.com/monosign/rest-api' },
          { label: 'REST API integrations', description: 'Wire into in-house systems', href: 'https://help.monofor.com/monosign/rest-api-integrations' },
          { label: 'API-based sign-in', description: 'Service-account auth flow', href: 'https://help.monofor.com/monosign/api-based-sign-in' },
          { label: 'Monosync API objects', description: 'Create / update objects via API', href: 'https://help.monofor.com/monosync/how-to-create-update-an-object-on-monosync-using-m' },
        ],
      },
      {
        title: 'Custom apps',
        links: [
          { label: 'Custom REST application', description: 'Add a custom app via REST', href: 'https://help.monofor.com/monosign/custom-application-rest-api-integration' },
          { label: 'Custom OpenID Connect app', description: 'Register an OIDC client', href: 'https://help.monofor.com/monosign/custom-openid-oidc-application' },
          { label: 'Generic browser actions', description: 'Fallback for apps without federation', href: 'https://help.monofor.com/monosign/generic-browser-actions' },
          { label: 'Browser-based sign-in', href: 'https://help.monofor.com/monosign/browser-based-sign-in' },
        ],
      },
      {
        title: 'Platform & deployment',
        links: [
          { label: 'Installation guide', href: 'https://help.monofor.com/monosign/installation-guide' },
          { label: 'Configuration guide', href: 'https://help.monofor.com/monosign/configuration-guide' },
          { label: 'Monopam operator (Kubernetes)', href: 'https://help.monofor.com/monopam/monofor-pam-operator' },
          { label: 'External Secrets Operator', href: 'https://help.monofor.com/monopam/external-secrets-operator' },
        ],
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Monosync API', description: 'Identity as a queryable resource.', href: '/sync/api' },
      { label: 'Single Sign-On', description: 'SAML 2 / OIDC / OAuth 2 on day one.', href: '/sign/sso' },
      { label: 'Integrations catalog', description: 'Every named integration in one place.', href: '/integrations' },
    ],
  },
  tr: {
    metaTitle: 'Geliştiriciler için Monofor',
    metaDescription:
      'OAuth 2, OIDC, SAML, JWT, SCIM ve temiz bir REST API. Kendi altyapınızda veya bulutta: aynı uç noktalar, aynı yapı, tedarikçiye kilitlenme yok.',
    heroEyebrow: 'Geliştiriciler için',
    heroTitle: ['Sizi uğraştırmayan', 'kimlik API’leri.'],
    heroBody:
      'OAuth 2, OIDC, SAML, JWT, SCIM ve temiz bir REST yüzeyi: kendi altyapınızda veya bulutta aynı yapı. Önce standartlar. SDK cambazlığı yok.',
    badges: ['OAuth 2 / OIDC / SAML 2 / JWT', 'OpenAPI 3 REST yüzeyi', 'Özel REST bağlayıcısı'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    routineEyebrow: 'Monofor ile bir gün',
    routineTitle: 'Bir geliştiricinin Monofor’lu haftası.',
    routineBody:
      'Kimlik doğrulamayla boğuşmaya daha az, ürün geliştirmeye daha çok zaman. Kimlik, betiklerle yönetebileceğiniz bir backend hâline gelir.',
    moments: [
      {
        time: 'Salı',
        title: 'Yeni kurum içi araca SSO ekleyin',
        body: 'OIDC’yi seçiyor, bir Monosign istemcisi kaydediyor, keşif URL’sini framework’ünüze yapıştırıyorsunuz ve yönlendirme akışı olduğu gibi çalışıyor. Beş dakikalık kurulum, öğrenilecek SDK yok.',
      },
      {
        time: 'Perşembe',
        title: 'Kod olarak kimliği CI’a bağlayın',
        body: 'Kullanıcı ve grup sağlamayı REST API üzerinden betikliyor, idempotency anahtarlarına sabitliyor ve pipeline’ınızda çalıştırıyorsunuz. Her ortam varsayılan olarak doğru erişimle geliyor.',
      },
      {
        time: 'Cuma',
        title: 'Denetim akışını SIEM’e bağlayın',
        body: 'Bir webhook aboneliği kimlik olaylarını yakalıyor; imzalarını doğrulayıp doğrudan SIEM’inize gönderiyorsunuz. Toplu işler yok, sürekli sorgulama yok.',
      },
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Geliştiricilerin Monofor’la elde ettikleri.',
    pillarsBody:
      'Monofor’u bir tedarikçi değil bir servis gibi hissettiren dört özellik: standartlar, REST, özel uygulama dostu yaklaşım ve dağıtım eşitliği.',
    pillars: [
      {
        title: 'Önce standartlar, özel token yok',
        description:
          'OAuth 2, OpenID Connect, SAML 2, JWT, SCIM: zaten bildiğiniz protokoller. Monofor’u ekleyin, kimlik doğrulama kodunuz taşınabilir kalsın.',
        bullets: [
          'OAuth 2 authorization-code, PKCE, client-credentials',
          'Keşif (discovery) ve JWKS ile OpenID Connect',
          'ADFS uyumlu doğrulama ifadeleriyle SAML 2',
          'JWT üretimi ve doğrulaması',
        ],
      },
      {
        title: 'Gerçekten betik yazabileceğiniz bir REST API',
        description:
          'Kullanıcılar, gruplar, yetkiler, cihazlar, denetimler: hepsine temiz bir REST yüzeyinden ulaşılır. OpenAPI spesifikasyonu, imleç tabanlı sayfalama, idempotency anahtarları.',
        bullets: [
          'İndirilebilir OpenAPI 3 spesifikasyonu',
          'Güvenli yeniden denemeler için idempotency anahtarları',
          'İmleç tabanlı sayfalama, öngörülebilir filtreler',
          'Kimlik değişikliklerinde webhook olayları',
        ],
      },
      {
        title: 'Özel uygulamalar bir öğleden sonrada bağlanır',
        description:
          'Katalogda olmayan bir kurum içi uygulamanız mı var? Özel REST entegrasyonu, HTTP konuşan her uygulamayı bir SSO hedefine dönüştürür.',
        bullets: [
          'Özel REST uygulama bağlayıcısı',
          'Özel OIDC uygulama desteği',
          'Eski uygulamalar için tarayıcı eklentisi yedeği',
          'Servis hesapları için API tabanlı oturum açma',
        ],
      },
      {
        title: 'Kendi altyapınızda veya bulutta aynı yapı',
        description:
          'Yerel bir Monosign kiracısına karşı geliştirin, kendi VPC’nize gönderin veya yönetilen bulut kiracısı olarak çalıştırın: aynı uç noktalar, aynı yapı, aynı kimlik doğrulama.',
        bullets: [
          'Docker, Helm ve fiziksel sunucu kurulumu',
          'Kubernetes yerlisi Monopam operatörü',
          'CI için sandbox kiracıları',
          'Kod olarak yapılandırma desenleri',
        ],
      },
    ],
    helpEyebrow: 'Doğrudan dokümantasyona',
    helpTitle: 'Doğrudan dokümanların içine.',
    helpBody:
      'Mühendislerin PR’lara gerçekten yapıştırdığı yardım makalelerine derin bağlantılar.',
    helpGroups: [
      {
        title: 'Standartlar ve protokoller',
        links: [
          { label: 'OAuth 2 entegrasyonu', description: 'Authorization-code akışı rehberi', href: 'https://help.monofor.com/monosign/oauth-integration' },
          { label: 'OpenID Connect entegrasyonları', description: 'OIDC kurulumu ve sağlayıcılar', href: 'https://help.monofor.com/monosign/openid-oidc-integrations' },
          { label: 'SAML 2.0 entegrasyonları', description: 'SAML kurulumu ve sağlayıcılar', href: 'https://help.monofor.com/monosign/saml-2.0-integrations' },
          { label: 'JWT entegrasyonları', description: 'JWT üretimi ve doğrulaması', href: 'https://help.monofor.com/monosign/jwt-integrations' },
        ],
      },
      {
        title: 'API’ler ve otomasyon',
        links: [
          { label: 'REST API genel bakış', description: 'Uç noktalar, kimlik doğrulama, kurallar', href: 'https://help.monofor.com/monosign/rest-api' },
          { label: 'REST API entegrasyonları', description: 'Kurum içi sistemlere bağlayın', href: 'https://help.monofor.com/monosign/rest-api-integrations' },
          { label: 'API tabanlı oturum açma', description: 'Servis hesabı kimlik doğrulama akışı', href: 'https://help.monofor.com/monosign/api-based-sign-in' },
          { label: 'Monosync API nesneleri', description: 'API ile nesne oluşturun / güncelleyin', href: 'https://help.monofor.com/monosync/how-to-create-update-an-object-on-monosync-using-m' },
        ],
      },
      {
        title: 'Özel uygulamalar',
        links: [
          { label: 'Özel REST uygulaması', description: 'REST ile özel uygulama ekleyin', href: 'https://help.monofor.com/monosign/custom-application-rest-api-integration' },
          { label: 'Özel OpenID Connect uygulaması', description: 'OIDC istemcisi kaydedin', href: 'https://help.monofor.com/monosign/custom-openid-oidc-application' },
          { label: 'Genel tarayıcı eylemleri', description: 'Federasyonu olmayan uygulamalar için yedek', href: 'https://help.monofor.com/monosign/generic-browser-actions' },
          { label: 'Tarayıcı tabanlı oturum açma', href: 'https://help.monofor.com/monosign/browser-based-sign-in' },
        ],
      },
      {
        title: 'Platform ve dağıtım',
        links: [
          { label: 'Kurulum rehberi', href: 'https://help.monofor.com/monosign/installation-guide' },
          { label: 'Yapılandırma rehberi', href: 'https://help.monofor.com/monosign/configuration-guide' },
          { label: 'Monopam operatörü (Kubernetes)', href: 'https://help.monofor.com/monopam/monofor-pam-operator' },
          { label: 'External Secrets Operator', href: 'https://help.monofor.com/monopam/external-secrets-operator' },
        ],
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Monosync API', description: 'Sorgulanabilir bir kaynak olarak kimlik.', href: '/sync/api' },
      { label: 'Tek Oturum Açma', description: 'İlk günden SAML 2 / OIDC / OAuth 2.', href: '/sign/sso' },
      { label: 'Entegrasyon kataloğu', description: 'Tüm adlandırılmış entegrasyonlar tek yerde.', href: '/integrations' },
    ],
  },
}

type Props = { locale: string }

export default function DevelopersPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/for/developers"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <PersonaRoutine
          eyebrow={c.routineEyebrow}
          title={c.routineTitle}
          body={c.routineBody}
          moments={c.moments}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={c.pillarsTitle}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <PersonaHelpLinks
          eyebrow={c.helpEyebrow}
          title={c.helpTitle}
          body={c.helpBody}
          groups={c.helpGroups}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
